/**
 * ประมวลผลรูปฝั่ง client ก่อนอัปโหลด
 *
 * ลำดับ: อ่านไฟล์ -> แก้ orientation -> ย่อ -> บีบอัดเป็น JPEG/WebP -> คำนวณ SHA-256
 *
 * เรื่อง EXIF: การวาดลง canvas แล้ว export ใหม่ ทำให้ metadata เดิมทั้งหมดหายไปโดยปริยาย
 * ซึ่งเป็นสิ่งที่เราต้องการ — พิกัดที่บันทึกต้องมาจาก Geolocation API เท่านั้น
 * ไม่ใช่จาก EXIF ของกล้องซึ่งอาจเป็นค่าเก่าหรือถูกแก้ได้
 */
import type { ImageSettings } from '../../shared/config';
import { sha256Hex } from '../../shared/hash';

export interface ProcessedImage {
  blob: Blob;
  contentType: 'image/jpeg' | 'image/webp';
  sizeBytes: number;
  sha256: string;
  width: number;
  height: number;
  /** URL สำหรับ preview — ผู้เรียกต้อง revoke เมื่อเลิกใช้ */
  previewUrl: string;
}

export class ImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageError';
  }
}

/**
 * โหลดรูปเป็น bitmap พร้อมแก้การหมุนตาม EXIF
 * createImageBitmap รองรับ imageOrientation ในเบราว์เซอร์ยุคปัจจุบันทั้งหมด
 * ถ้าไม่รองรับจะ fallback ไปใช้ <img> ซึ่งเบราว์เซอร์หมุนให้เองอยู่แล้ว
 */
async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in globalThis) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // HEIC บางเครื่องถอดรหัสผ่าน createImageBitmap ไม่ได้ ให้ตกไปทาง <img>
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () =>
        reject(new ImageError('เปิดไฟล์รูปนี้ไม่ได้ กรุณาถ่ายใหม่ หรือเลือกไฟล์ JPEG/PNG แทน'));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function dimensionsOf(source: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  if ('naturalWidth' in source) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new ImageError('บีบอัดรูปไม่สำเร็จ กรุณาลองถ่ายใหม่'));
      },
      type,
      quality,
    );
  });
}

/**
 * ย่อและบีบอัดรูปให้เข้าเป้า
 *
 * ถ้ารอบแรกยังใหญ่เกิน hard limit จะลดคุณภาพลงทีละขั้นแล้วลองใหม่
 * ทำได้สูงสุด 4 รอบ เพื่อไม่ให้เครื่องรุ่นเก่าค้างนานเกินไป
 */
export async function processImage(
  file: File | Blob,
  settings: ImageSettings,
): Promise<ProcessedImage> {
  if (file.size === 0) {
    throw new ImageError('ไฟล์ที่เลือกว่างเปล่า กรุณาถ่ายใหม่');
  }

  const source = await loadBitmap(file);
  const { width: sourceWidth, height: sourceHeight } = dimensionsOf(source);

  if (sourceWidth === 0 || sourceHeight === 0) {
    throw new ImageError('อ่านขนาดของรูปไม่ได้ กรุณาถ่ายใหม่');
  }

  const longEdge = Math.max(sourceWidth, sourceHeight);
  const scale = longEdge > settings.maxLongEdgePx ? settings.maxLongEdgePx / longEdge : 1;
  const width = Math.round(sourceWidth * scale);
  const height = Math.round(sourceHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new ImageError('เบราว์เซอร์นี้ประมวลผลรูปไม่ได้');

  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, width, height);
  if ('close' in source) source.close();

  const contentType = settings.outputContentType;

  /*
   * บีบอัดจนถึง targetMaxBytes ไม่ใช่แค่ให้ต่ำกว่า maxBytes
   *
   * maxBytes เป็นเพดานแข็งไว้กันกรณีเลวร้ายสุด ส่วน targetMaxBytes คือค่าที่ใช้
   * คำนวณงบพื้นที่จัดเก็บทั้งโครงการ (200,000 x 250 KB = ประมาณ 50 GB)
   * ถ้าหยุดบีบอัดตั้งแต่ต่ำกว่าเพดาน ไฟล์เฉลี่ยจะโตกว่าที่วางแผนไว้เกือบเท่าตัว
   * และค่าจัดเก็บจริงจะไม่ตรงกับที่ตกลงกับผู้ว่าจ้าง
   *
   * ภาพที่มีรายละเอียดมากอาจลดคุณภาพจนต่ำสุดแล้วยังไม่ถึงเป้า
   * กรณีนั้นย่อขนาดภาพลงแล้ววนใหม่ ซึ่งได้ผลกว่าการลดคุณภาพต่อ
   */
  const MIN_QUALITY = 0.4;
  const QUALITY_STEPS = 6;
  const DIMENSION_STEPS = 3;
  const DIMENSION_FACTOR = 0.85;

  let workingCanvas = canvas;
  let workingWidth = width;
  let workingHeight = height;
  let quality = settings.quality;
  let blob = await canvasToBlob(workingCanvas, contentType, quality);

  for (let shrink = 0; shrink <= DIMENSION_STEPS; shrink++) {
    quality = settings.quality;
    for (let attempt = 0; attempt < QUALITY_STEPS; attempt++) {
      blob = await canvasToBlob(workingCanvas, contentType, quality);
      if (blob.size <= settings.targetMaxBytes) break;
      if (quality <= MIN_QUALITY) break;
      // ลดแรงตอนที่ยังห่างเป้ามาก แล้วค่อยละเอียดขึ้นเมื่อใกล้ถึง
      const overshoot = blob.size / settings.targetMaxBytes;
      quality = Math.max(MIN_QUALITY, Number((quality - (overshoot > 2 ? 0.15 : 0.08)).toFixed(2)));
    }
    if (blob.size <= settings.targetMaxBytes || shrink === DIMENSION_STEPS) break;

    workingWidth = Math.max(640, Math.round(workingWidth * DIMENSION_FACTOR));
    workingHeight = Math.max(480, Math.round(workingHeight * DIMENSION_FACTOR));
    workingCanvas = redraw(canvas, workingWidth, workingHeight);
  }

  if (blob.size > settings.maxBytes) {
    throw new ImageError(
      `รูปยังมีขนาด ${formatBytes(blob.size)} หลังบีบอัดแล้ว ซึ่งเกินเพดาน ${formatBytes(settings.maxBytes)} กรุณาถ่ายใหม่โดยให้ภาพมีรายละเอียดน้อยลง`,
    );
  }

  const buffer = await blob.arrayBuffer();
  const sha256 = await sha256Hex(buffer);

  return {
    blob,
    contentType,
    sizeBytes: blob.size,
    sha256,
    width: workingWidth,
    height: workingHeight,
    previewUrl: URL.createObjectURL(blob),
  };
}

/** วาดภาพเดิมลง canvas ใหม่ที่เล็กลง ใช้เมื่อลดคุณภาพจนสุดแล้วยังไม่ถึงขนาดเป้าหมาย */
function redraw(source: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement {
  const next = document.createElement('canvas');
  next.width = width;
  next.height = height;
  const context = next.getContext('2d', { alpha: false });
  if (!context) throw new ImageError('เบราว์เซอร์นี้ประมวลผลรูปไม่ได้');
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, width, height);
  return next;
}

/** ชนิดไฟล์ที่ให้เลือกได้จาก input — server ตรวจจากไบต์จริงอีกชั้นเสมอ */
export function acceptAttribute(settings: ImageSettings): string {
  return settings.acceptedInputTypes.join(',');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** เตือนเมื่อไฟล์ออกนอกช่วงที่ตั้งเป้าไว้ ไม่ได้ห้าม แค่ให้ผู้ใช้รู้ */
export function sizeAdvice(sizeBytes: number, settings: ImageSettings): string | null {
  if (sizeBytes < settings.targetMinBytes) {
    return 'ไฟล์เล็กกว่าปกติ ตรวจว่าภาพยังชัดพอให้เห็นรายละเอียดหน้างาน';
  }
  if (sizeBytes > settings.targetMaxBytes) {
    return 'ไฟล์ใหญ่กว่าปกติ อาจใช้เวลาส่งนานขึ้นเมื่อสัญญาณอ่อน';
  }
  return null;
}
